import React, { ChangeEvent, Dispatch, FormEvent, FunctionComponent, KeyboardEvent, SetStateAction } from 'react';
import { FlashBox } from '../../atoms/flashBox';
import {
  CarefulRegistrationProps,
  ConditionForm,
  FieldErrors,
  HistoryItem,
  InformationForm,
  SelectOption,
  Step,
} from './types';

type Props = Pick<CarefulRegistrationProps, 'assets' | 'companyMessage' | 'links' | 'mode' | 'options' | 'tokenErrorMessage'> & {
  condition: ConditionForm;
  careerHistories: HistoryItem[];
  educationalHistories: HistoryItem[];
  errors: FieldErrors;
  flash: boolean;
  flashMessage: string;
  flashType: string;
  handleFlash: (flash: boolean, type: string, message: string) => void;
  information: InformationForm;
  informationExpanded: boolean;
  interestInput: string;
  interests: string[];
  occupationDetails: SelectOption[];
  preferredOccupationDetails: SelectOption[];
  skillInput: string;
  skills: string[];
  step: Step;
  showScout: boolean;
  submitting: boolean;
  addCareerHistory: () => void;
  addEducationalHistory: () => void;
  addTag: (type: 'skill' | 'interest', value: string) => void;
  removeTag: (type: 'skill' | 'interest', value: string) => void;
  setCondition: Dispatch<SetStateAction<ConditionForm>>;
  setInterestInput: (value: string) => void;
  setSkillInput: (value: string) => void;
  submitCondition: (event: FormEvent) => void;
  submitHistory: (event: FormEvent) => void;
  submitInformation: (event: FormEvent) => void;
  submitSkill: (event: FormEvent) => void;
  tagKeyDown: (type: 'skill' | 'interest') => (event: KeyboardEvent<HTMLInputElement>) => void;
  updateHistory: (
    type: 'education' | 'career',
    index: number,
    field: keyof HistoryItem,
    value: string | boolean,
  ) => void;
  updateInformation: (field: keyof InformationForm, value: string | boolean) => void;
};

const errorText = (errors: FieldErrors, keys: string[], fallback: string) => {
  const messages = Array.from(new Set(keys.flatMap(key => errors[key] || [])));
  return messages.length ? messages.join(' / ') : fallback;
};

const Select: FunctionComponent<{
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  required?: boolean;
}> = ({ value, onChange, options, placeholder, className, disabled, id, required }) => {
  const hasBlankOption = options.some(option => option.value === '');

  return (
    <select id={id} className={className} value={value} onChange={event => onChange(event.target.value)} disabled={disabled} required={required}>
      {!hasBlankOption && <option value="">{placeholder}</option>}
      {options.map((option, index) => (
        <option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const ErrorMessage: FunctionComponent<{ show: boolean; children: React.ReactNode }> = ({ show, children }) => (
  show ? <p className="newpeople-form-td-insert alert alert-danger">{children}</p> : null
);

export const Presentation: FunctionComponent<Props> = ({
  assets,
  mode,
  companyMessage,
  tokenErrorMessage,
  condition,
  careerHistories,
  educationalHistories,
  errors,
  flash,
  flashMessage,
  flashType,
  handleFlash,
  information,
  informationExpanded,
  interestInput,
  interests,
  links,
  occupationDetails,
  options,
  preferredOccupationDetails,
  skillInput,
  skills,
  step,
  showScout,
  submitting,
  addCareerHistory,
  addEducationalHistory,
  addTag,
  removeTag,
  setCondition,
  setInterestInput,
  setSkillInput,
  submitCondition,
  submitHistory,
  submitInformation,
  submitSkill,
  tagKeyDown,
  updateHistory,
  updateInformation,
}) => {
  const statusImages = {
    information: [assets.informationStatusPc, assets.informationStatusSp],
    history: [assets.historyStatusPc, assets.historyStatusSp],
    skill: [assets.skillStatusPc, assets.skillStatusSp],
    condition: [assets.conditionStatusPc, assets.conditionStatusSp],
  }[step];
  const isBasic = mode === 'basic';
  const contentClassName = isBasic ? 'newpeople' : 'basic-information';

  return (
    <>
      {isBasic && (
        <header className="l-header l-header-responsive u-clear">
          <div className="l-header-logo">
            <a className="l-header-logo-link pc-logo" href="/">
              <img src={assets.logoPc} className="l-header-logo-img" alt="READY TO FASHION" width="244" />
            </a>
            <a className="l-header-logo-link sp-logo" href="/">
              <img src={assets.logoSp} className="l-header-logo-img" alt="READY TO FASHION" width="244" />
            </a>
          </div>
        </header>
      )}
      <div className="l-wrap l-wrap-old l-wrap-newpeople u-clear">
        <div className="l-newpeople">
          <div className="l-newpeople-inner">
            <div className={contentClassName}>
            {isBasic ? (
              <>
                <h2 className="newpeople-heading">READY TO FASHION に会員登録</h2>
                {companyMessage && <h3 className="newpeople-heading-bottom">{companyMessage}</h3>}
                {tokenErrorMessage && <h3 className="newpeople-heading-bottom-alert">{tokenErrorMessage}</h3>}
                <div className="newpeople-status">
                  <img src={assets.basicStatus} alt="はじめる→完了" />
                </div>
              </>
            ) : (
              <>
                <img src={statusImages[0]} alt="基本プロフィール→履歴書・職務経歴書" className="basic-information-status-pc" />
                <img src={statusImages[1]} alt="基本プロフィール→履歴書・職務経歴書" className="basic-information-status-sp" />
              </>
            )}
            <FlashBox flash={flash} flashType={flashType} flashMessage={flashMessage} handleFlash={handleFlash} />

            {step === 'information' && (
              <>
                {!isBasic && (
                  <h2 className="basic-information-heading">
                    まずはあなたの基本的な<br className="basic-information-br" />プロフィールを記入しましょう
                  </h2>
                )}
                <div className="newpeople-form">
                  <form onSubmit={submitInformation} noValidate>
                    <div className="newpeople-form-tr u-clear" id="careful-field-last_name">
                      <div className="newpeople-form-th u-clear">
                        <label>氏名</label>
                        <span>必須</span>
                      </div>
                      <div className="newpeople-form-td newpeople-form-td-name">
                        <input id="last_name-input" className="form-control" placeholder="（姓）高野" value={information.lastName} onChange={event => updateInformation('lastName', event.target.value)} required />
                        <input id="first_name-input" className="form-control" placeholder="（名）聡司" value={information.firstName} onChange={event => updateInformation('firstName', event.target.value)} required />
                      </div>
                      <ErrorMessage show={!!(errors.last_name || errors.first_name)}>必須項目が未入力です</ErrorMessage>
                    </div>

                    <div className="newpeople-form-tr u-clear" id="careful-field-birthday">
                      <div className="newpeople-form-th u-clear">
                        <label>生年月日</label>
                        <span>必須</span>
                      </div>
                      <div className="newpeople-form-td birthday js-date-group">
                        <Select id="birthday_1i" className="js-date-year" value={information.birthdayYear} onChange={value => updateInformation('birthdayYear', value)} options={options.birthdayYears} placeholder="西暦" required />
                        <span className="birthday--word">年</span>
                        <Select id="birthday_2i" className="js-date-month" value={information.birthdayMonth} onChange={value => updateInformation('birthdayMonth', value)} options={options.months} placeholder="月" required />
                        <span className="birthday--word">月</span>
                        <Select id="birthday_3i" className="js-date-day" value={information.birthdayDay} onChange={value => updateInformation('birthdayDay', value)} options={options.days} placeholder="日" required />
                        <span className="birthday--word">日</span>
                        <ErrorMessage show={!!errors.birthday}>{errorText(errors, ['birthday'], '必須項目が未入力です')}</ErrorMessage>
                      </div>
                    </div>

                    {informationExpanded && <div>
                      <div className="newpeople-form-tr u-clear" id="careful-field-occupation_id">
                        <div className="newpeople-form-th u-clear"><label>直近の職種（大カテゴリー）</label><span>必須</span></div>
                        <div className="newpeople-form-td newpeople-form-td-select">
                          <Select className="form-control" value={information.occupationId} onChange={value => updateInformation('occupationId', value)} options={options.occupations} placeholder="" required />
                          <p className="newpeople-form-notice">※在学中の方は学生を選択してください</p>
                          <ErrorMessage show={!!errors.occupation_id}>必須項目が未入力です</ErrorMessage>
                        </div>
                      </div>
                      {information.occupationId !== '15' && (
                        <div className="newpeople-form-tr padding u-clear">
                          <div className="newpeople-form-th u-clear"><label>直近の職種（小カテゴリー）</label></div>
                          <div className="newpeople-form-td newpeople-form-td-select">
                            <Select
                              className="form-control low"
                              value={information.occupationDetailId}
                              onChange={value => updateInformation('occupationDetailId', value)}
                              options={occupationDetails}
                              placeholder=""
                              disabled={!information.occupationId || information.occupationId === '14'}
                            />
                          </div>
                        </div>
                      )}
                      {information.occupationId === '15' && (
                        <div className="newpeople-form-tr padding u-clear" id="careful-field-graduated_at">
                          <div className="newpeople-form-th u-clear"><label>卒業予定年</label><span>必須</span></div>
                          <div className="newpeople-form-td newpeople-form-td-select">
                            <Select id="graduated_at-input" className="form-control" value={information.graduatedAt} onChange={value => updateInformation('graduatedAt', value)} options={options.graduatedAts} placeholder="" />
                            <ErrorMessage show={!!errors.graduated_at}>必須項目が未入力です</ErrorMessage>
                          </div>
                        </div>
                      )}
                      <div className="newpeople-form-tr u-clear" id="careful-field-preferred_occupation_id">
                        <div className="newpeople-form-th u-clear"><label>希望職種（大カテゴリー）</label><span>必須</span></div>
                        <div className="newpeople-form-td newpeople-form-td-select">
                          <Select className="form-control" value={information.preferredOccupationId} onChange={value => updateInformation('preferredOccupationId', value)} options={options.preferredOccupations} placeholder="" required />
                          <ErrorMessage show={!!errors.preferred_occupation_id}>必須項目が未入力です</ErrorMessage>
                        </div>
                      </div>
                      <div className="newpeople-form-tr padding u-clear">
                        <div className="newpeople-form-th u-clear"><label>希望職種（小カテゴリー）</label></div>
                        <div className="newpeople-form-td newpeople-form-td-select">
                          <Select
                            className="form-control low"
                            value={information.preferredOccupationDetailId}
                            onChange={value => updateInformation('preferredOccupationDetailId', value)}
                            options={preferredOccupationDetails}
                            placeholder=""
                            disabled={!information.preferredOccupationId}
                          />
                        </div>
                      </div>
                      <div className="newpeople-form-tr u-clear" id="careful-field-prefecture_id">
                        <div className="newpeople-form-th u-clear"><label>希望勤務地</label><span>必須</span></div>
                        <div className="newpeople-form-td newpeople-form-td-select">
                          <Select className="form-control" value={information.prefectureId} onChange={value => updateInformation('prefectureId', value)} options={options.prefectures} placeholder="" required />
                          <ErrorMessage show={!!errors.prefecture_id}>必須項目が未入力です</ErrorMessage>
                        </div>
                      </div>
                      {showScout && <div className="newpeople-form-tr u-clear" id="careful-field-scout_flag">
                        <div className="newpeople-form-th u-clear"><label>スカウト</label><span>必須</span></div>
                        <div className="newpeople-form-td newpeople-form-td-select">
                          <Select value={information.scoutFlag} onChange={value => updateInformation('scoutFlag', value)} options={options.scoutFlags} placeholder="" />
                          <ErrorMessage show={!!errors.scout_flag}>必須項目が未入力です</ErrorMessage>
                        </div>
                      </div>}
                      <div className="newpeople-form-tr u-clear" id="careful-field-email">
                        <div className="newpeople-form-th newpeople-form-th--top u-clear"><label>メールアドレス</label><span>必須</span></div>
                        <div className="newpeople-form-td">
                          <input type="email" className="form-control" id="email-input" placeholder="（例）takano@example.co.jp" value={information.email} onChange={event => updateInformation('email', event.target.value)} required />
                          <ErrorMessage show={!!errors.email}>必須項目が未入力か、既に登録してあるメールアドレスです</ErrorMessage>
                          <p className="newpeople-form-notice">※企業との連絡手段にもなるので正確なメールアドレスで設定してください</p>
                        </div>
                      </div>
                      {!information.provider && (
                        <div className="newpeople-form-tr padding u-clear" id="careful-field-password">
                          <div className="newpeople-form-th u-clear"><label>パスワード</label><span>必須</span></div>
                          <div className="newpeople-form-td">
                            <input type="password" className="form-control" id="password-input" value={information.password} onChange={event => updateInformation('password', event.target.value)} required minLength={6} />
                            <ErrorMessage show={!!errors.password}>必須項目が未入力です</ErrorMessage>
                            <p className="newpeople-form-notice">※パスワードは6文字以上にしてください</p>
                          </div>
                        </div>
                      )}
                    </div>}
                    {informationExpanded && <div id="careful-field-agreement">
                      <div className="newpeople-statements">
                        <input id="agreement" name="agreement" type="checkbox" className="newpeople-statements-checkbox" checked={information.agreement} onChange={event => updateInformation('agreement', event.target.checked)} required />
                        <label className="newpeople-statements-label" htmlFor="agreement">
                          <a href={links.terms} target="_blank" rel="noreferrer">ユーザー向け利用規約</a>、
                          <a href={links.privacy} target="_blank" rel="noreferrer">プライバシーポリシー</a>に同意します。
                        </label>
                      </div>
                      <ErrorMessage show={!!errors.agreement}>規約に同意してください</ErrorMessage>
                    </div>}
                    <div className="newpeople-submit">
                      <button type="submit" className="button-agree center" disabled={submitting}>登録 / 次へ</button>
                    </div>
                    <p className="newpeople-info">※お預かりしたお客様の個人情報は、弊社からお客様へのご連絡のために使用します。法令に定める場合を除き、第三者に対して個人情報の提供・開示する事はありません。</p>
                  </form>
                </div>
              </>
            )}

            {step === 'history' && (
              <>
                <h2 className="basic-information-heading">あなたの経歴を教えてください</h2>
                <div className="newpeople-form">
                  <form onSubmit={submitHistory} noValidate>
                    <h2 className="history-form-title">-学歴-</h2>
                    {educationalHistories.map((history, index) => (
                      <div className="history-form js-educational-history" key={`education-${index}`}>
                        <div className="history-form-tr u-clear">
                          <div className="history-form-th u-clear"><label>学校名</label></div>
                          <div className="newpeople-form-td"><input className="form-control" value={history.name} onChange={event => updateHistory('education', index, 'name', event.target.value)} /></div>
                        </div>
                        <div className="history-form-tr u-clear">
                          <div className="history-form-th u-clear"><label>学部・学科</label></div>
                          <div className="newpeople-form-td"><input className="form-control" value={history.department} onChange={event => updateHistory('education', index, 'department', event.target.value)} /></div>
                        </div>
                        <PeriodSelect item={history} startYears={options.educationalStartYears} endYears={options.educationalEndYears} months={options.months} onChange={(field, value) => updateHistory('education', index, field, value)} />
                      </div>
                    ))}
                    <p className="history-form-add-careers-form">
                      <button type="button" className="history-form-add-careers-form js-add-profile-form" onClick={addEducationalHistory}>
                        <i className="fas fa-plus"></i> 学歴を追加
                      </button>
                    </p>
                    <h2 className="history-form-title">-職歴-</h2>
                    {careerHistories.map((history, index) => (
                      <div className="history-form js-career-histories" key={`career-${index}`}>
                        <div className="history-form-tr u-clear">
                          <div className="history-form-th u-clear"><label>会社名</label></div>
                          <div className="newpeople-form-td"><input className="form-control" value={history.name} onChange={event => updateHistory('career', index, 'name', event.target.value)} /></div>
                        </div>
                        <div className="history-form-tr u-clear">
                          <div className="history-form-th u-clear"><label>部署・役職</label></div>
                          <div className="newpeople-form-td"><input className="form-control" value={history.position} onChange={event => updateHistory('career', index, 'position', event.target.value)} /></div>
                        </div>
                        <PeriodSelect item={history} startYears={options.careerStartYears} endYears={options.careerEndYears} months={options.months} onChange={(field, value) => updateHistory('career', index, field, value)} />
                        <div className="history-form-current_working-tr u-clear">
                          <div className="history-form-current_working-th u-clear">
                            <input type="checkbox" checked={!!history.currentWorking} onChange={event => updateHistory('career', index, 'currentWorking', event.target.checked)} />
                            <label className="history-form-current_working-th-label">現在働いている</label>
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="history-form-add-careers-form">
                      <button type="button" className="history-form-add-careers-form js-add-profile-form" onClick={addCareerHistory}>
                        <i className="fas fa-plus"></i> 職歴を追加
                      </button>
                    </p>
                    <div className="newpeople-statements"></div>
                    <div className="newpeople-submit"><button type="submit" className="button-agree center js-user-register" disabled={submitting}>次へ</button></div>
                  </form>
                </div>
              </>
            )}

            {step === 'skill' && (
              <>
                <h2 className="basic-information-heading">あなたの興味・関心のあること・<br className="basic-information-br" />スキルを教えてください</h2>
                <div className="newpeople-form js-l-profile-edit js-ajax-recommend-tag-if-edit-page">
                  <form onSubmit={submitSkill} noValidate>
                    <TagSection title="- スキル・特徴・資格 -" id="careful-field-skills" tags={skills} input={skillInput} setInput={setSkillInput} onAdd={value => addTag('skill', value)} onRemove={value => removeTag('skill', value)} onKeyDown={tagKeyDown('skill')} recommend={options.recommendSkills} placeholder="スキル・特徴・資格を入力して確定キー" error={errors.skills?.[0]} />
                    <TagSection title="- 興味・関心 -" id="careful-field-interests" tags={interests} input={interestInput} setInput={setInterestInput} onAdd={value => addTag('interest', value)} onRemove={value => removeTag('interest', value)} onKeyDown={tagKeyDown('interest')} recommend={options.recommendInterests} placeholder="興味・関心を入力して確定キー" error={errors.interests?.[0]} />
                    <div className="newpeople-statements"></div>
                    <div className="newpeople-submit"><button type="submit" className="button-agree center js-user-register js-disable-btn-for-preservation" disabled={submitting}>次へ</button></div>
                  </form>
                </div>
              </>
            )}

            {step === 'condition' && (
              <div className="newpeople-form">
                <form onSubmit={submitCondition} noValidate>
                  <h2 className="history-form-title">仕事探しの条件を教えてください</h2>
                  <div className="history-form">
                    <div className="history-form-tr u-clear">
                      <div className="history-form-th u-clear"><label>モチベーション</label></div>
                      <div className="newpeople-form-td">
                        <Select className="form_control" value={condition.motivation} onChange={value => setCondition(prev => ({ ...prev, motivation: value }))} options={options.motivations} placeholder="" />
                      </div>
                    </div>
                  </div>
                  <h2 className="history-form-title">自己PRをしてください</h2>
                  <div className="history-form">
                    <div className="history-form-tr u-clear" id="careful-field-introduction">
                      <div className="conditions-form-th u-clear"><label>紹介文</label></div>
                      <div className="newpeople-form-td">
                        <textarea className="conditions-form-area" maxLength={2000} value={condition.introduction} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setCondition(prev => ({ ...prev, introduction: event.target.value }))} />
                        <ErrorMessage show={!!errors.introduction}>2000文字以内にしてください。</ErrorMessage>
                      </div>
                    </div>
                    <div className="history-form-tr u-clear" id="careful-field-slogan">
                      <div className="conditions-form-th u-clear"><label>挑戦したいこと</label></div>
                      <div className="newpeople-form-td">
                        <textarea className="conditions-form-area" maxLength={2000} value={condition.slogan} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setCondition(prev => ({ ...prev, slogan: event.target.value }))} />
                        <ErrorMessage show={!!errors.slogan}>2000文字以内にしてください。</ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="newpeople-statements"></div>
                  <div className="newpeople-submit"><button type="submit" className="button-agree center js-user-register" disabled={submitting}>登録完了</button></div>
                </form>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PeriodSelect: FunctionComponent<{
  item: HistoryItem;
  startYears: SelectOption[];
  endYears: SelectOption[];
  months: SelectOption[];
  onChange: (field: keyof HistoryItem, value: string) => void;
}> = ({ item, startYears, endYears, months, onChange }) => (
  <div className="history-form-tr-period u-clear">
    <div className="history-form-th u-clear"><label>在籍期間</label></div>
    <div className="newpeople-form-td">
      <div className="history-form-td"><Select className="history-form-year" value={item.startYear} onChange={value => onChange('startYear', value)} options={startYears} placeholder="西暦" /><h2 className="history-form-year-date">年</h2></div>
      <div className="history-form-td"><Select className="history-form-month" value={item.startMonth} onChange={value => onChange('startMonth', value)} options={months} placeholder="月" /><h2 className="history-form-month-date">月</h2></div>
      <div className="history-form-while">~</div>
      <div className="history-form-td"><Select className="history-form-year" value={item.endYear} onChange={value => onChange('endYear', value)} options={endYears} placeholder="西暦" /><h2 className="history-form-year-date">年</h2></div>
      <div className="history-form-td"><Select className="history-form-month" value={item.endMonth} onChange={value => onChange('endMonth', value)} options={months} placeholder="月" /><h2 className="history-form-month-date">月</h2></div>
    </div>
  </div>
);

const TagSection: FunctionComponent<{
  title: string;
  id: string;
  tags: string[];
  input: string;
  setInput: (value: string) => void;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  recommend: string[];
  placeholder: string;
  error?: string;
}> = ({ title, id, tags, input, setInput, onAdd, onRemove, onKeyDown, recommend, placeholder, error }) => (
  <>
    <h2 className="history-form-title">{title}</h2>
    <div className="skill-form" id={id}>
      <div className="skill-form-container">
        <div className="skill-form-contents">
          <div className="profile-edit-add-tag-myself">
            <ul className="profile-edit-add-tag-myself-list js-my-tag-list">
              {tags.map(tag => (
                <li className="profile-attribute-list-item profile-edit-add-tag-myself-list-item" key={tag}>
                  {tag}
                  <button type="button" className="profile-edit-add-tag-myself-list-item-delete" onClick={() => onRemove(tag)} aria-label={`${tag}を削除`}></button>
                </li>
              ))}
            </ul>
            <span className="profile-edit-add-tag-myself-punctuate"></span>
            <input type="text" className="profile-edit-add-tag-myself-search js-search-tag" placeholder={placeholder} value={input} onChange={event => setInput(event.target.value)} onKeyDown={onKeyDown} onBlur={() => onAdd(input)} />
          </div>
          <ErrorMessage show={!!error}>{error}</ErrorMessage>
          <div className="profile-edit-add-tag-recommend">
            <p className="profile-edit-add-tag-recommend-example">例えばこんなのも…(クリックorタッチで入力)</p>
            <ul className="profile-edit-add-tag-recommend-list js-recommend-tag-list">
              {recommend.map(tag => (
                <li className="profile-attribute-list-item profile-edit-add-tag-myself-list-item" key={tag} onClick={() => onAdd(tag)}>
                  {tag}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </>
);
